import { useAuthStore } from "@/stores/auth";
import { setupLayouts } from "virtual:generated-layouts";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { routesMode } from "@appmode/routes";
import { canAccessReportingRoute } from "@/composables/reporting/useReportingPermissions";
import { useNotificationStore } from "@/stores/notification";

declare module "vue-router" {
  interface RouteMeta {
    layout?: string;
    requiresAuth?: boolean; // defaults to true
    requiresAccount?: boolean; // excludes guest (proto) member sessions
    reporting?: boolean;
  }
}

const routesBase: RouteRecordRaw[] = [];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: setupLayouts(routesBase.concat(routesMode)),
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Check if route requires authentication
  const requiresAuth =
    to.meta.requiresAuth === undefined ? true : to.meta.requiresAuth; // Default to true

  if (!requiresAuth) {
    // Public route, allow access
    return next();
  }

  // For protected routes, verify authentication
  if (
    !authStore.isAuthenticated() ||
    (to.meta.requiresAccount && authStore.isProtoMember)
  ) {
    return next({
      path: "/login",
      query: { redirect: to.fullPath },
    });
  }

  // Handle token refresh if needed
  if (authStore.refreshRequired()) {
    try {
      await authStore.refresh();
    } catch (error) {
      // A rejected refresh already cleared the session; transient failures keep it.
      if (!authStore.isAuthenticated())
        return next({
          path: "/login",
          query: { redirect: to.fullPath },
        });
    }
  }

  if (!canAccessReportingRoute(to.meta.reporting, authStore)) {
    useNotificationStore().addSnackbar("Insufficient Permissions", "error");
    return next({ path: "/" });
  }

  if (authStore.user === null) {
    await authStore.getUserInfo();
  }

  // Authentication valid, proceed
  return next();
});

export default router;
